import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { isDemoMode } from '../utils/demoMode';
import { useDemoPermissions } from '../hooks/useDemoPermissions';
import { getFranchiseeById, editFranchisee, createXeroContactFromFranchisee, previewXeroContactFromFranchisee, updateFranchiseeCompliance } from '../services/franchiseeService';
import { uploadFiles, uploadSingleFile, uploadCompliancePoliceCertificate, deleteFile, getSignedUrl } from '../services/fileService';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaFileUpload, FaPaperclip, FaTimes, FaFileAlt, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileArchive, FaFileVideo, FaFileAudio, FaFileCode, FaCalendarAlt } from 'react-icons/fa';
import { fetchFileTypes, isCustomFileType, filterAttachmentFileTypes } from '../config/fileTypes';
import ConfirmationModal from '../components/ConfirmationModal';
import { InputField, TextareaField } from '../components/fields';

const EditFranchiseePage = () => {
  const { id: franchiseeId } = useParams();
  const navigate = useNavigate();
  const { isDemo } = useDemoPermissions();
  // Demo mode redirect
  useEffect(() => { if (isDemo) navigate('/franchisees'); }, [isDemo, navigate]);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState(null);
  const [canUpdateFseCode, setCanUpdateFseCode] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);

  // Save confirmation modal state
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const [xeroContactIdWillChange, setXeroContactIdWillChange] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // File upload state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, file: null });
  const [deleting, setDeleting] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const fileInputRef = useRef(null);

  // Compliance data state
  const [complianceData, setComplianceData] = useState(null);
  const [editableExpiryDate, setEditableExpiryDate] = useState('');
  // Track original expiry to detect changes for enabling Save Changes button
  const [savedExpiryDate, setSavedExpiryDate] = useState('');
  const [complianceFileUploading, setComplianceFileUploading] = useState(null); // null | 'police' | 'currency'
  const [complianceFileError, setComplianceFileError] = useState(null);
  const [complianceCertChanged, setComplianceCertChanged] = useState(false);
  const policeCertFileInputRef = useRef(null);
  const currencyFileInputRef = useRef(null);

  // Xero contact creation state
  const [xeroCreationLoading, setXeroCreationLoading] = useState(false);
  const [xeroCreationError, setXeroCreationError] = useState(null);
  const [xeroCreationSuccess, setXeroCreationSuccess] = useState(null);
  const [xeroPreviewLoading, setXeroPreviewLoading] = useState(false);
  const [xeroPreviewError, setXeroPreviewError] = useState(null);
  const [xeroPreviewModal, setXeroPreviewModal] = useState({ show: false, data: null });

  const mapFranchiseeToFormData = (data = {}) => ({
    franchiseCode: data.fseCode || '',
    franchiseName: data.franchiseName || '',
    email: data.email || '',
    phoneNumber: data.phoneNumber || '',
    abn: data.abn || '',
    gstRegistered: data.gstRegistered ? 'Yes' : 'No',
    country: data.country || 'Australia',
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    bsbNumber: data.bsbNumber || '',
    bankAccountNumber: data.bankAccountNumber || '',
    bankAccountName: data.bankAccountName || '',
    territory: data.territory || '',
    addressLine1: data.addressLine1 || '',
    addressLine2: data.addressLine2 || '',
    state: data.state || '',
    postalCode: data.postalCode || '',
    xeroContactId: data.xeroContactId || '',
    isExternal: Boolean(data.isExternal),
  });

  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting, isDirty } } = useForm({
    mode: 'onBlur',
    defaultValues: mapFranchiseeToFormData()
  });

  useEffect(() => {
    const fetchFranchisee = async () => {
      setLoading(true);
      setApiError(null);
      try {
        const data = await getFranchiseeById(franchiseeId);
        const formData = mapFranchiseeToFormData(data);
        setInitialData(formData);
        setCanUpdateFseCode(data.canUpdateFseCode || false);
        reset(formData);
        
        // Load existing files if they exist
        if (data.attachments && Array.isArray(data.attachments)) {
          setUploadedFiles(data.attachments.map(file => ({
            id: file.id,
            name: file.fileName,
            url: file.url,
            size: file.size || 0,
            category: file.type || 'other', 
            customType: file.customType || '',
            expiryDate: file.expiryDate || ''
          })));
        } else {
          setUploadedFiles([]);
        }

        // Load compliance data - always initialize so the section is visible
        const complianceInfo = data.compliance || {};
        setComplianceData(complianceInfo);
        const expiry = complianceInfo.policeCertificateExpiryDate || '';
        setEditableExpiryDate(expiry);
        setSavedExpiryDate(expiry);
      } catch (error) {
        setApiError(error.message || 'Failed to load franchisee details.');
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFranchisee();
  }, [franchiseeId, reset]);

  // Load file types on component mount
  useEffect(() => {
    const loadFileTypes = async () => {
      try {
        const types = await fetchFileTypes()
        setFileTypes(filterAttachmentFileTypes(types));
      } catch (error) {
        console.error('Error loading file types:', error);
        // Set fallback values
        setFileTypes([{ value: 'other', label: 'Other' }]);
      } finally {
      }
    };

    loadFileTypes();
  }, []);

  useEffect(() => {
    let timer;
    if (saveSuccessMessage) {
      timer = setTimeout(() => {
        setSaveSuccessMessage(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [saveSuccessMessage]);

  const onSubmit = async (data) => {
    setApiError(null);
    setSaveSuccessMessage(null);

    const payload = { ...data };
    if (canUpdateFseCode && data.franchiseCode && data.franchiseCode !== initialData.franchiseCode) {
      payload.fseCode = data.franchiseCode;
    }
    delete payload.franchiseCode;
    delete payload.country;
    delete payload.compliance;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.status;
    if (!(canUpdateFseCode && data.franchiseCode && data.franchiseCode !== initialData.franchiseCode)) {
      delete payload.fseCode;
    }
    delete payload.id;

    if (data.xeroContactId) {
        payload.xeroContactId = data.xeroContactId;
    } else {
        delete payload.xeroContactId;
    }

    // Include file attachments
    payload.attachments = uploadedFiles.map(file => ({
      id: file.id,
      fileName: file.name,
      url: file.url,
      size: file.size,
      type: file.category || 'other',
      customType: file.customType || null,
      expiryDate: file.expiryDate || null
    }));

    // Detect if xeroContactId is changing
    const currentXeroContactId = initialData?.xeroContactId || '';
    const nextXeroContactId = data.xeroContactId || '';
    const xeroChanging = currentXeroContactId !== nextXeroContactId;

    setPendingSaveData(payload);
    setXeroContactIdWillChange(xeroChanging);
    setShowSaveConfirmModal(true);
  };

  const performActualSave = async () => {
    if (!pendingSaveData) return;
    setShowSaveConfirmModal(false);
    setIsSaving(true);
    setApiError(null);
    setSaveSuccessMessage(null);

    try {
      // Save franchisee fields and expiry date in parallel when expiry has changed
      const savePromises = [editFranchisee(franchiseeId, pendingSaveData)];
      if (editableExpiryDate !== savedExpiryDate || complianceCertChanged) {
        savePromises.push(
          updateFranchiseeCompliance(franchiseeId, { policeCertificateExpiryDate: editableExpiryDate || null })
            .then(() => {
              setComplianceData(prev => ({ ...prev, policeCertificateExpiryDate: editableExpiryDate }));
              setSavedExpiryDate(editableExpiryDate);
              setComplianceCertChanged(false);
            })
        );
      }
      const [updatedFranchisee] = await Promise.all(savePromises);
      const updatedFormData = mapFranchiseeToFormData(updatedFranchisee);
      reset(updatedFormData);
      setInitialData(updatedFormData);
      const xeroMsg = xeroContactIdWillChange
        ? ' Unpaid RCTIs are being updated with the new Xero Contact ID in the background.'
        : '';
      setSaveSuccessMessage(`Franchisee updated successfully!${xeroMsg}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setApiError(error.message || 'Failed to update franchisee. Please try again.');
      console.error('Update error:', error);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
      setPendingSaveData(null);
      setXeroContactIdWillChange(false);
    }
  };

  const handlePreviewXeroContact = async () => {
    setXeroPreviewLoading(true);
    setXeroPreviewError(null);
    setXeroCreationError(null);
    setXeroCreationSuccess(null);

    try {
      const result = await previewXeroContactFromFranchisee(franchiseeId);
      setXeroPreviewModal({ show: true, data: result });
    } catch (error) {
      setXeroPreviewError(error.message || 'Failed to preview Xero contact');
    } finally {
      setXeroPreviewLoading(false);
    }
  };

  // Xero contact creation/linking handler
  const handleCreateXeroContact = async () => {
    setXeroCreationLoading(true);
    setXeroCreationError(null);
    setXeroCreationSuccess(null);

    try {
      const result = await createXeroContactFromFranchisee(franchiseeId);

      // Update the form with the new xeroContactId
      const updatedFormData = {
        ...initialData,
        xeroContactId: result.xeroContactId
      };
      reset(updatedFormData);
      setInitialData(updatedFormData);

      // Show different messages based on action and whether it was a sync
      const wasSync = initialData?.xeroContactId;
      const xeroIdChanged = initialData?.xeroContactId !== result.xeroContactId;

      if (wasSync) {
        if (xeroIdChanged) {
          setXeroCreationSuccess(`✓ Xero Contact ID updated in Loope (New ID: ${result.xeroContactId})`);
        } else {
          setXeroCreationSuccess(`✓ Xero contact verified - already in sync (ID: ${result.xeroContactId})`);
        }
      } else if (result.action === 'linked') {
        setXeroCreationSuccess(`✓ Linked to existing Xero contact (ID: ${result.xeroContactId})`);
      } else {
        setXeroCreationSuccess(`✓ Created new Xero contact (ID: ${result.xeroContactId})`);
      }

      // Clear success message after 5 seconds
      setTimeout(() => setXeroCreationSuccess(null), 5000);

      return true;
    } catch (error) {
      setXeroCreationError(error.message || 'Failed to process Xero contact');
      return false;
    } finally {
      setXeroCreationLoading(false);
    }
  };

  const handleCloseXeroPreviewModal = () => {
    setXeroPreviewModal({ show: false, data: null });
  };

  const formatXeroPhoneList = (phones = []) => {
    if (!phones.length) return '';
    const formatted = phones
      .map(phone => {
        const phoneType = phone.PhoneType || phone.phoneType || 'Phone';
        const phoneNumber = phone.PhoneNumber || phone.phoneNumber || '';
        return phoneNumber ? `${phoneType}: ${phoneNumber}` : '';
      })
      .filter(Boolean);
    return formatted.join(', ');
  };

  const formatXeroAddressList = (addresses = []) => {
    if (!addresses.length) return '';
    const formatted = addresses
      .map(address => {
        const parts = [
          address.AddressLine1 || address.addressLine1,
          address.AddressLine2 || address.addressLine2,
          address.City || address.city,
          address.Region || address.region,
          address.PostalCode || address.postalCode
        ].filter(Boolean);
        return parts.join(', ');
      })
      .filter(Boolean);
    return formatted.join(' | ');
  };

  const renderXeroPreviewField = (label, value) => (
    <div className="flex justify-between gap-4">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <span className="text-sm text-gray-900 text-right break-all">{value || '—'}</span>
    </div>
  );

  // File upload functions
  const handleAttachFile = () => {
    fileInputRef.current.click();
  };

  // Check if files have changed from initial state
  const hasFileChanges = () => {
    if (!initialData) return false;
    
    const initialFiles = initialData.attachments || [];
    
    // Check if the number of files changed
    if (uploadedFiles.length !== initialFiles.length) return true;
    
    // Check if any file IDs are different
    const currentFileIds = uploadedFiles.map(f => f.id).sort();
    const initialFileIds = initialFiles.map(f => f.id).sort();
    
    return JSON.stringify(currentFileIds) !== JSON.stringify(initialFileIds);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-red-500" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-blue-500" />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel className="text-green-500" />;
      case 'ppt':
      case 'pptx':
        return <FaFilePowerpoint className="text-orange-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return <FaFileImage className="text-purple-500" />;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return <FaFileArchive className="text-yellow-500" />;
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return <FaFileVideo className="text-pink-500" />;
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'aac':
        return <FaFileAudio className="text-indigo-500" />;
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'html':
      case 'css':
      case 'json':
      case 'xml':
        return <FaFileCode className="text-gray-600" />;
      default:
        return <FaFileAlt className="text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    files.forEach(file => {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }

      const newFile = {
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        category: fileTypes[0]?.value || 'other',
        customType: '',
        expiryDate: ''
      };

      console.log('newFile:', newFile)

      setAttachedFiles(prev => [...prev, newFile]);
    });

    console.log('attachedFiles:', attachedFiles)

    // Clear the input
    event.target.value = '';
    setUploadError(null);
  };

  const updateFileCategory = (fileId, category) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, category, customType: category === 'other' ? file.customType : '' } : file
      )
    );
  };

  const updateFileCustomType = (fileId, customType) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, customType } : file
      )
    );
  };

  const updateFileExpiryDate = (fileId, expiryDate) => {
    setAttachedFiles(prev =>
      prev.map(file =>
        file.id === fileId ? { ...file, expiryDate } : file
      )
    );
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleViewDocument = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const handlePoliceCertFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setComplianceFileUploading('police');
    setComplianceFileError(null);
    try {
      const result = await uploadCompliancePoliceCertificate(file);
      await updateFranchiseeCompliance(franchiseeId, { policeCertificate: result.url });
      setComplianceData(prev => ({ ...prev, policeCertificate: result.url }));
      setComplianceCertChanged(true);
      // Auto-fill expiry whenever the server extracts a date from the PDF
      if (result.expiryDate) {
        setEditableExpiryDate(result.expiryDate);
      }
    } catch (err) {
      setComplianceFileError('Failed to upload police certificate: ' + (err.message || 'Unknown error'));
    } finally {
      setComplianceFileUploading(null);
    }
  };

  const handleCurrencyFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setComplianceFileUploading('currency');
    setComplianceFileError(null);
    try {
      const result = await uploadSingleFile({ file, category: 'certificate-of-currency' });
      const url = result?.data?.urls?.[0] || result?.urls?.[0];
      if (!url) throw new Error('No URL returned from upload.');
      await updateFranchiseeCompliance(franchiseeId, { certificateOfCurrency: url });
      setComplianceData(prev => ({ ...prev, certificateOfCurrency: url }));
    } catch (err) {
      setComplianceFileError('Failed to upload certificate of currency: ' + (err.message || 'Unknown error'));
    } finally {
      setComplianceFileUploading(null);
    }
  };

  const removeUploadedFile = (fileId) => {
    const fileToDelete = uploadedFiles.find(file => file.id === fileId);
    if (fileToDelete) {
      setDeleteConfirmModal({ show: true, file: fileToDelete });
    }
  };

  const confirmDeleteFile = async () => {
    if (!deleteConfirmModal.file) return;
    
    setDeleting(true);
    try {
      await deleteFile(deleteConfirmModal.file.url);
      setUploadedFiles(prev => prev.filter(file => file.id !== deleteConfirmModal.file.id));
      setDeleteConfirmModal({ show: false, file: null });
    } catch (error) {
      console.error('Error deleting file:', error);
      setUploadError(error.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteFile = () => {
    setDeleteConfirmModal({ show: false, file: null });
  };

  const handleUploadFiles = async () => {
    if (attachedFiles.length === 0) return;

    // Check if all files have required fields
    const filesWithoutCategory = attachedFiles.filter(file => !file.category.trim());
    if (filesWithoutCategory.length > 0) {
      setUploadError('Please select a file type for all files before uploading.');
      return;
    }

    const filesWithoutCustomType = attachedFiles.filter(file =>
      file.category === 'other' && !file.customType.trim()
    );
    if (filesWithoutCustomType.length > 0) {
      setUploadError('Please specify the custom type for "other" file types before uploading.');
      return;
    }

    const filesWithoutExpiryDate = attachedFiles.filter(file => !file.expiryDate);
    if (filesWithoutExpiryDate.length > 0) {
      setUploadError('Please provide an expiry date for all files before uploading.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const uploadData = attachedFiles.map(file => ({
        file: file.file,
        category: file.category,
        customType: file.customType,
        expiryDate: file.expiryDate,
        entityType: 'franchisee',
        entityId: franchiseeId
      }));

      const response = await uploadFiles(uploadData);
      
      // Handle the response structure - response.data.urls contains the URLs
      const urls = response.data?.urls || [];
      
      // Add uploaded files to the uploaded files list
      const newUploadedFiles = attachedFiles.map((file, index) => ({
        id: Date.now() + Math.random() + index,
        name: file.name,
        url: urls[index] || '',
        size: file.size || 0,
        category: file.category,
        customType: file.customType,
        expiryDate: file.expiryDate
      }));

      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
      setAttachedFiles([]); // Clear attached files
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError(error.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
    }
  };

  if (loading) {
    return <p className="text-center text-gray-500 py-10">Loading franchisee details...</p>;
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-20 max-w-3xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Edit Franchisee</h1>
        <Link to="/franchisees" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="bg-white p-8 rounded-3xl shadow-md space-y-6">

        {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}
        {saveSuccessMessage && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded flex items-center">
            <FaCheckCircle className="mr-2" />
            {saveSuccessMessage}
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-1">
            <label htmlFor="franchiseCode" className="block text-sm font-medium text-gray-700">Franchise Code {canUpdateFseCode && '*'}</label>
            <div className="relative group">
              <input
                id="franchiseCode"
                type="text"
                {...register("franchiseCode", { required: "Franchise Code is required" })}
                readOnly={!canUpdateFseCode}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm ${
                  !canUpdateFseCode ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'border-gray-300 focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan'
                }`}
              />
              {!canUpdateFseCode && (
                <div className="absolute z-10 w-64 p-2 mt-1 text-sm text-white bg-gray-800 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
                  This FSE Code cannot be edited because it's already linked to existing jobs, invoices, or RCTIs. Use the FSE Code Update Utility instead.
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <InputField label="Franchise Name *" id="franchiseName"
              register={register("franchiseName", { required: "Franchise Name is required." })}
              error={errors.franchiseName}
            />
          </div>
        </div>

        {/* External Franchisee Checkbox */}
        <div className="border-t pt-4 mt-4 border-gray-200">
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="isExternal"
                    {...register("isExternal")}
                    className="h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300 rounded"
                />
                <label htmlFor="isExternal" className="ml-2 block text-sm text-gray-700">
                    External Franchisee
                </label>
                <span className="ml-2 text-xs text-gray-500">
                    (Check if this franchisee is external to the region)
                </span>
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <InputField label="First Name *" id="firstName"
              register={register("firstName", { required: "First Name is required." })}
              error={errors.firstName}
            />
          </div>
          <div>
            <InputField label="Last Name *" id="lastName"
              register={register("lastName", { required: "Last Name is required." })}
              error={errors.lastName}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <InputField label="Email *" id="email" type="email"
              register={register("email", { required: "Email is required.", pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address." } })}
              error={errors.email}
            />
          </div>
          <div>
            <InputField label="Phone Number *" id="phoneNumber" type="tel"
              register={register("phoneNumber", { required: "Phone Number is required." })}
              error={errors.phoneNumber}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <InputField label="ABN *" id="abn"
              register={register("abn", { required: "ABN is required." })}
              error={errors.abn}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">GST Registered *</label>
            <div className="mt-2 flex items-center space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="Yes"
                  {...register("gstRegistered", { required: "Please select GST registration status." })}
                  className="form-radio h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="No"
                  {...register("gstRegistered", { required: "Please select GST registration status." })}
                  className="form-radio h-4 w-4 text-green-600 focus:ring-green-600 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
            {errors.gstRegistered && <p className="mt-1 text-xs text-red-600">{errors.gstRegistered.message}</p>}
          </div>
        </div>

        {/* BSB and Bank Account Number Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <InputField label="BSB Number *" id="bsbNumber"
              register={register("bsbNumber", { required: "BSB Number is required." })}
              error={errors.bsbNumber}
            />
          </div>
          <div>
            <InputField label="Bank Account Number *" id="bankAccountNumber"
              register={register("bankAccountNumber", { required: "Bank Account Number is required." })}
              error={errors.bankAccountNumber}
            />
          </div>
        </div>

        {/* Bank Account Name Row */}
        <div>
          <InputField label="Bank Account Name *" id="bankAccountName"
            register={register("bankAccountName", { required: "Bank Account Name is required." })}
            error={errors.bankAccountName}
          />
        </div>

        {/* Xero Contact ID Field - always editable */}
        <div>
            <InputField label="Xero Contact ID" id="xeroContactId"
                register={register("xeroContactId")}
                placeholder="Enter Xero Contact ID (optional)"
            />
        </div>

        {/* Xero Integration Section */}
        {!isDemoMode() && (
        <div className="">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Xero Integration</h3>

          {!initialData?.xeroContactId && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handlePreviewXeroContact}
                disabled={xeroPreviewLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-green-600 hover:bg-green-600-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {xeroPreviewLoading ? 'Checking...' : 'Create in Xero'}
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Creates a new Xero contact or links to an existing one if found (matched by Franchisee Code)
              </p>
            </div>
          )}

          {initialData?.xeroContactId && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center justify-between">
              <p className="text-sm text-green-800">
                <strong>Xero Contact ID:</strong> {initialData.xeroContactId}
              </p>
              <button
                type="button"
                onClick={handlePreviewXeroContact}
                disabled={xeroPreviewLoading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-600-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {xeroPreviewLoading ? 'Checking...' : 'Sync'}
              </button>
            </div>
          )}

          {xeroCreationError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{xeroCreationError}</p>
            </div>
          )}

          {xeroPreviewError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{xeroPreviewError}</p>
            </div>
          )}

          {xeroCreationSuccess && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-2xl p-4">
              <p className="text-sm text-green-800">{xeroCreationSuccess}</p>
            </div>
          )}
        </div>
        )}

        <div className="border-t pt-6 mt-6 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <InputField label="Territory" id="territory"
                register={register("territory")}
              />
            </div>
            <div>
              <InputField label="Address Line 1" id="addressLine1"
                register={register("addressLine1")}
              />
            </div>
            <div>
              <InputField label="Address Line 2" id="addressLine2"
                register={register("addressLine2")}
              />
            </div>
            <div>
              <InputField label="State" id="state"
                register={register("state")}
              />
            </div>
            <div>
              <InputField label="Postal Code" id="postalCode"
                register={register("postalCode")}
              />
            </div>
            <div>
              <InputField label="Country" id="country"
                register={register("country")}
                readOnly disabled value="Australia"
                className="bg-gray-100 cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="border-t pt-6 mt-6 border-gray-200">
          <p className="text-sm text-gray-600 mb-3">File Attachments:</p>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="*/*"
          />

          {/* Attach File Link */}
          <div className="mb-4">
            <button
              type="button"
              onClick={handleAttachFile}
              className="inline-flex items-center text-green-600 hover:text-gray-900 font-medium text-sm"
            >
              <FaPaperclip className="mr-2" />
              Attach File
            </button>
            <span className="ml-2 text-xs text-gray-500">(Max 10MB per file)</span>
          </div>

          {/* Display attached files (to be uploaded) */}
          {attachedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Files to Upload:</h4>
              {attachedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        File Type *
                      </label>
                      <select
                        value={file.category}
                        onChange={(e) => updateFileCategory(file.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-green-600 focus:border-green-600"
                      >
                        {fileTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {isCustomFileType(file.category) && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Custom Type *
                        </label>
                        <input
                          type="text"
                          value={file.customType}
                          onChange={(e) => updateFileCustomType(file.id, e.target.value)}
                          placeholder="Specify the document type"
                          className="w-full px-3 py-2 text-sm rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-green-600 focus:border-green-600"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Expiry Date *
                      </label>
                      <input
                        type="date"
                        value={file.expiryDate}
                        onChange={(e) => updateFileExpiryDate(file.id, e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-2xl border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-green-600 focus:border-green-600"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Upload Files Button - right after files to upload */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleUploadFiles}
                  disabled={uploading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-full hover:bg-green-600-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaFileUpload className="mr-2" />
                  {uploading ? 'Uploading...' : `Upload ${attachedFiles.length} File(s)`}
                </button>
              </div>
            </div>
          )}

          {/* Display uploaded files */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-700">Uploaded Files:</h4>
              {uploadedFiles.map((file) => (
                <div key={file.id} className="p-4 bg-green-50 rounded-2xl border border-green-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="text-lg">
                        {getFileIcon(file.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => handleViewDocument(file.url)}
                          className="text-sm font-medium text-green-600 hover:text-gray-900 underline truncate block text-left"
                          title="Click to download file"
                        >
                          {file.name}
                        </button>
                        <p className="text-xs text-gray-500">
                          {file.size > 0 ? formatFileSize(file.size) : 'Size unknown'} •
                          Type: {file.category === 'other' && file.customType ? file.customType : fileTypes.find(type => type.value === file.category)?.label}
                          {file.expiryDate && ` • Expires: ${new Date(file.expiryDate).toLocaleDateString()}`}
                        </p>
                        <p className="text-xs text-green-600 font-medium">
                          ✓ Uploaded successfully
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeUploadedFile(file.id)}
                      className="text-red-500 hover:text-red-700 p-1 ml-2"
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-md">
              {uploadError}
            </div>
          )}
        </div>

        {/* Confirmation Modal for File Deletion */}
        {deleteConfirmModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirm File Deletion
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete "{deleteConfirmModal.file?.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-full hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteFile}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-full hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {xeroPreviewModal.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="relative mx-auto p-8 w-full border border-gray-100 max-w-2xl shadow-lg rounded-3xl bg-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {initialData?.xeroContactId
                      ? 'Sync Xero Contact'
                      : (xeroPreviewModal.data?.exists ? 'Existing Xero Contact Found' : 'Create New Xero Contact')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {initialData?.xeroContactId
                      ? 'Verify the Xero contact matches this franchisee.'
                      : (xeroPreviewModal.data?.exists
                        ? 'This franchisee already exists in Xero. The Xero Contact ID will be updated in Loope to link both systems.'
                        : 'No matching contact was found in Xero. Review what will be created.')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseXeroPreviewModal}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mt-6 space-y-3">
                {xeroPreviewModal.data?.exists && xeroPreviewModal.data?.existingContact && (
                  <div className="space-y-2">
                    {renderXeroPreviewField('Contact ID', xeroPreviewModal.data.existingContact.ContactID)}
                    {renderXeroPreviewField('Name', xeroPreviewModal.data.existingContact.Name)}
                    {renderXeroPreviewField('Account Number', xeroPreviewModal.data.existingContact.AccountNumber)}
                    {renderXeroPreviewField('Email', xeroPreviewModal.data.existingContact.EmailAddress)}
                    {renderXeroPreviewField('Phones', formatXeroPhoneList(xeroPreviewModal.data.existingContact.Phones))}
                    {renderXeroPreviewField('Addresses', formatXeroAddressList(xeroPreviewModal.data.existingContact.Addresses))}
                  </div>
                )}

                {/* Sync status indicator when IDs don't match */}
                {initialData?.xeroContactId && xeroPreviewModal.data?.exists &&
                 xeroPreviewModal.data?.existingContact?.ContactID !== initialData.xeroContactId && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> The Xero Contact ID in Loope ({initialData.xeroContactId}) differs from the one found in Xero ({xeroPreviewModal.data.existingContact.ContactID}).
                      Clicking "Confirm Sync" will update Loope with the correct ID.
                    </p>
                  </div>
                )}

                {/* Already in sync indicator */}
                {initialData?.xeroContactId && xeroPreviewModal.data?.exists &&
                 xeroPreviewModal.data?.existingContact?.ContactID === initialData.xeroContactId && (
                  <div className="rounded-md bg-green-50 border border-green-200 p-3">
                    <p className="text-sm text-green-800">
                      <strong>✓ In Sync:</strong> The Xero Contact ID in Loope matches the one in Xero.
                    </p>
                  </div>
                )}

                {!xeroPreviewModal.data?.exists && xeroPreviewModal.data?.wouldCreateContact && (
                  <div className="space-y-2">
                    {renderXeroPreviewField('Name', xeroPreviewModal.data.wouldCreateContact.Name)}
                    {renderXeroPreviewField('First Name', xeroPreviewModal.data.wouldCreateContact.FirstName)}
                    {renderXeroPreviewField('Last Name', xeroPreviewModal.data.wouldCreateContact.LastName)}
                    {renderXeroPreviewField('Account Number', xeroPreviewModal.data.wouldCreateContact.AccountNumber)}
                    {renderXeroPreviewField('Email', xeroPreviewModal.data.wouldCreateContact.EmailAddress)}
                    {renderXeroPreviewField('ABN', xeroPreviewModal.data.wouldCreateContact.TaxNumber)}
                    {renderXeroPreviewField('Phone', formatXeroPhoneList(xeroPreviewModal.data.wouldCreateContact.Phones))}
                    {renderXeroPreviewField('Bank Account', xeroPreviewModal.data.wouldCreateContact.BankAccountDetails)}
                  </div>
                )}

                {!!xeroPreviewModal.data?.missingFields?.length && (
                  <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                    <p className="text-sm text-yellow-800">
                      Missing required fields: {xeroPreviewModal.data.missingFields.join(', ')}.
                      Please update the franchisee record before linking.
                    </p>
                  </div>
                )}

                {xeroCreationError && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-800">{xeroCreationError}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseXeroPreviewModal}
                  disabled={xeroCreationLoading}
                  className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 transition duration-300 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const success = await handleCreateXeroContact();
                    if (success) {
                      handleCloseXeroPreviewModal();
                    }
                  }}
                  disabled={xeroCreationLoading || xeroPreviewModal.data?.canCreate === false}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-600-dark transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {xeroCreationLoading
                    ? (initialData?.xeroContactId ? 'Syncing...' : 'Linking...')
                    : (initialData?.xeroContactId
                        ? 'Confirm Sync'
                        : (xeroPreviewModal.data?.exists ? 'Link to Existing' : 'Create Contact'))}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Information Section */}
        {complianceData && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Information</h3>

            <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
              {/* Police Certificate */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Police Check Certificate</h4>
                <input
                  ref={policeCertFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={handlePoliceCertFileChange}
                />
                <div className="space-y-2">
                  {complianceData.policeCertificate ? (
                    <div className="flex items-center space-x-2">
                      <FaFilePdf className="text-red-500" />
                      <button
                        type="button"
                        onClick={() => handleViewDocument(complianceData.policeCertificate)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View Police Certificate
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No police certificate uploaded</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => policeCertFileInputRef.current?.click()}
                      disabled={complianceFileUploading === 'police'}
                      className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition duration-150"
                    >
                      <FaFileUpload className="text-gray-500" />
                      {complianceFileUploading === 'police' ? 'Uploading...' : complianceData.policeCertificate ? 'Replace File' : 'Upload File'}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <FaCalendarAlt className="text-gray-500 flex-shrink-0" />
                    <label className="text-sm text-gray-600 flex-shrink-0">Expiry date:</label>
                    <input
                      type="date"
                      value={editableExpiryDate}
                      onChange={(e) => setEditableExpiryDate(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-green-600 focus:border-green-600"
                    />
                    {editableExpiryDate && new Date(editableExpiryDate) < new Date() && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                        Expired
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Certificate of Currency */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Certificate of Currency</h4>
                <input
                  ref={currencyFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf,image/*"
                  className="hidden"
                  onChange={handleCurrencyFileChange}
                />
                <div className="space-y-2">
                  {complianceData.certificateOfCurrency ? (
                    <>
                      <div className="flex items-center space-x-2">
                        <FaFilePdf className="text-red-500" />
                        <button
                          type="button"
                          onClick={() => handleViewDocument(complianceData.certificateOfCurrency)}
                          className="text-blue-600 hover:underline text-sm"
                        >
                          View Certificate of Currency
                        </button>
                      </div>
                      {(complianceData.certificateOfCurrencyStartDate || complianceData.certificateOfCurrencyFinishDate) && (
                        <div className="flex items-center space-x-2 text-sm">
                          <FaCalendarAlt className="text-gray-500" />
                          <span className="text-gray-600">
                            {complianceData.certificateOfCurrencyStartDate && (
                              <>Start: {new Date(complianceData.certificateOfCurrencyStartDate).toLocaleDateString()}</>
                            )}
                            {complianceData.certificateOfCurrencyStartDate && complianceData.certificateOfCurrencyFinishDate && (
                              <> | </>
                            )}
                            {complianceData.certificateOfCurrencyFinishDate && (
                              <>Finish: {new Date(complianceData.certificateOfCurrencyFinishDate).toLocaleDateString()}</>
                            )}
                          </span>
                          {complianceData.certificateOfCurrencyFinishDate && new Date(complianceData.certificateOfCurrencyFinishDate) < new Date() && (
                            <span className="ml-2 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                              Expired
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No certificate of currency uploaded</p>
                  )}
                  <button
                    type="button"
                    onClick={() => currencyFileInputRef.current?.click()}
                    disabled={complianceFileUploading === 'currency'}
                    className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition duration-150"
                  >
                    <FaFileUpload className="text-gray-500" />
                    {complianceFileUploading === 'currency' ? 'Uploading...' : complianceData.certificateOfCurrency ? 'Replace File' : 'Upload File'}
                  </button>
                </div>
              </div>

              {/* Compliance file upload error */}
              {complianceFileError && (
                <p className="text-xs text-red-600 -mt-2">{complianceFileError}</p>
              )}

              {/* Influenza Vaccine Declaration */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Influenza Vaccine Declaration</h4>
                {complianceData.hasInfluenzaVaccine ? (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-green-500" />
                      <span className="text-sm text-gray-700">Declared received influenza vaccine</span>
                    </div>
                    {complianceData.influenzaVaccineDate && (
                      <div className="flex items-center space-x-2 text-sm ml-6">
                        <FaCalendarAlt className="text-gray-500" />
                        <span className="text-gray-600">
                          Date: {new Date(complianceData.influenzaVaccineDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No declaration made</p>
                )}
              </div>

              {/* LCHS Fact Sheet Acknowledgment */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">LCHS Aged Care Provider Training</h4>
                {complianceData.hasReadLCHSFactSheet ? (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-green-500" />
                      <span className="text-sm text-gray-700">Acknowledged reading LCHS fact sheet</span>
                    </div>
                    {complianceData.lchsFactSheetDate && (
                      <div className="flex items-center space-x-2 text-sm ml-6">
                        <FaCalendarAlt className="text-gray-500" />
                        <span className="text-gray-600">
                          Date: {new Date(complianceData.lchsFactSheetDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No acknowledgment made</p>
                )}
              </div>
            </div>
          </div>
        )}

         <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <div className="container mx-auto px-6 py-3 max-w-3xl flex justify-end gap-3">
          <Link to="/franchisees" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition duration-300">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || isSaving || (!isDirty && !hasFileChanges() && editableExpiryDate === savedExpiryDate && !complianceCertChanged)}
            className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-2xl shadow-sm focus:outline-none focus:shadow-outline transition duration-300 disabled:opacity-50"
          >
            {isSubmitting || isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          </div>
        </div>
      </form>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSaveConfirmModal}
        onClose={() => setShowSaveConfirmModal(false)}
        onConfirm={performActualSave}
        title="Confirm Save Changes"
        message={
          xeroContactIdWillChange
            ? 'Are you sure you want to save these changes? The Xero Contact ID is being updated — all unpaid RCTIs for this franchisee will also be updated with the new Xero Contact ID.'
            : 'Are you sure you want to save these changes?'
        }
      />
    </div>
  );
};

export default EditFranchiseePage;
